/* src/components/admin/UpdateCustomerModal.tsx */
import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { adminCustomerService, AdminCustomer, AdminUpdateCustomerData } from '../../services/admin.customer.service';
import { countryService, CountryFull } from '../../services/country.service';
import { utilsService, DniTypeObject, getDniCompositeKey, findDniTypeByCompositeKey, applyDniPrefix, getMaxLengthFromMask } from '../../services/utils.service';
import { errorService } from '../../services/error.service';
import useForm, { ValidationRules } from '../../hooks/useForm';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSave,
    faUser,
    faEnvelope,
    faLock,
    faCheck,
    faEye,
    faEyeSlash,
    faAddressCard,
    faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { formatPhoneNational, isValidPhone, getPhonePlaceholder } from '../../config/phone.utils';
import styles from '../../styles/components/UpdateCustomerModal.module.css';
import modalStyles from '../../styles/common/Modal.module.css';

interface UpdateCustomerModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
    customer: AdminCustomer | null;
}

const UpdateCustomerModal: React.FC<UpdateCustomerModalProps> = ({ show, onHide, onSuccess, customer }) => {
    const [countries, setCountries] = useState<CountryFull[]>([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [dniTypes, setDniTypes] = useState<DniTypeObject[]>([]);
    const [loadingDniTypes, setLoadingDniTypes] = useState(true);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Fetch countries list on mount
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                setLoadingCountries(true);
                const data = await countryService.getAllCountries();
                setCountries(data);
            } catch (error) {
                console.error("Error loading countries:", error);
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchCountries();
    }, []);

    const initialValues = React.useMemo<AdminUpdateCustomerData>(() => {
        if (!customer) {
            return {
                idCustomer: 0,
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                kindOfDNI: '',
                DNI: '',
                phoneNumber: '',
                isActive: true,
                isVerify: false,
                iso2Country: 'VE'
            };
        }

        const country = customer.isO2Country || 'VE';
        const countryObj = countries.find((c) => c.iso2Country === country) ?? null;
        const nationalPhone = formatPhoneNational(customer.phoneNumber || '', country, countryObj?.phoneCode);

        return {
            idCustomer: customer.id,
            firstName: customer.firstName || '',
            lastName: customer.lastName || '',
            email: customer.email || '',
            password: '',
            kindOfDNI: customer.kindOfDNI || '',
            DNI: customer.dni || '',
            phoneNumber: nationalPhone,
            isActive: customer.isActive,
            isVerify: customer.isVerify,
            iso2Country: country
        };
    }, [customer, countries]);

    const validationRules = React.useMemo<ValidationRules<AdminUpdateCustomerData>>(() => ({
        firstName: (val) => (!val?.trim() ? 'El nombre es obligatorio' : null),
        lastName: (val) => (!val?.trim() ? 'El apellido es obligatorio' : null),
        email: (val) => {
            if (!val?.trim()) return 'El correo electrónico es obligatorio';
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(val)) return 'Formato de correo inválido';
            return null;
        },
        password: (val) => {
            if (val && (val.length < 8 || val.length > 16)) {
                return 'Debe tener entre 8 y 16 caracteres';
            }
            return null;
        },
        DNI: (val, allValues) => {
            if (!val?.trim()) return 'El DNI es obligatorio';
            const selectedDniObj = findDniTypeByCompositeKey(dniTypes, allValues.kindOfDNI);
            if (selectedDniObj && selectedDniObj.mask) {
                const regex = new RegExp(selectedDniObj.mask);
                if (!regex.test(val)) {
                    return `El documento no coincide con el formato requerido para ${selectedDniObj.kindOfDNI}.`;
                }
            }
            return null;
        },
        phoneNumber: (val, allValues) => {
            if (!val?.trim()) return 'El número de teléfono es obligatorio';
            if (!isValidPhone(val, allValues.iso2Country || '')) return 'Número de teléfono inválido';
            return null;
        },
    }), [dniTypes]);

    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        resetForm,
        setValues,
        setErrors
    } = useForm<AdminUpdateCustomerData>(
        initialValues,
        validationRules
    );

    const selectedCountryObj = React.useMemo(
        () => countries.find((c) => c.iso2Country === values.iso2Country) ?? null,
        [countries, values.iso2Country]
    );

    // Fetch DNI Types dynamically on country change
    useEffect(() => {
        const fetchDniTypes = async () => {
            if (!values.iso2Country) return;
            try {
                setLoadingDniTypes(true);
                const types = await utilsService.getDniTypes(values.iso2Country);
                setDniTypes(types);

                // If it is the currently loaded customer and the country matches, keep their DNI type.
                // Otherwise, default to the first available DNI type.
                if (customer && customer.isO2Country === values.iso2Country) {
                    let matchedType: DniTypeObject | undefined = undefined;
                    if (values.iso2Country === 'VE' && customer.dni) {
                        const cleanDni = customer.dni.trim().toUpperCase();
                        matchedType = types.find(t => t.prefix && cleanDni.startsWith(t.prefix.toUpperCase()));
                    }
                    if (!matchedType) {
                        matchedType = types.find(t => t.kindOfDNI === customer.kindOfDNI || getDniCompositeKey(t) === customer.kindOfDNI);
                    }
                    const finalKey = matchedType ? getDniCompositeKey(matchedType) : (customer.kindOfDNI || '');
                    setValues(prev => ({ ...prev, kindOfDNI: finalKey }));
                } else if (types.length > 0) {
                    setValues(prev => ({ ...prev, kindOfDNI: getDniCompositeKey(types[0]) }));
                } else {
                    setValues(prev => ({ ...prev, kindOfDNI: '' }));
                }
            } catch (err) {
                console.error("Error fetching DNI types in UpdateCustomerModal:", err);
            } finally {
                setLoadingDniTypes(false);
            }
        };
        fetchDniTypes();
    }, [values.iso2Country, customer, setValues]);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const formatted = formatPhoneNational(val, values.iso2Country || '', selectedCountryObj?.phoneCode);
        setValues((prev: any) => ({ ...prev, phoneNumber: formatted }));
        
        const error = validationRules.phoneNumber(formatted, { ...values, phoneNumber: formatted });
        setErrors((prev: any) => ({
            ...prev,
            phoneNumber: error
        }));
    };

    const handleDniChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const selectedDniObj = findDniTypeByCompositeKey(dniTypes, values.kindOfDNI);
        const formatted = applyDniPrefix(val, selectedDniObj?.mask);
        setValues((prev: any) => ({ ...prev, DNI: formatted }));

        const error = validationRules.DNI(formatted, { ...values, DNI: formatted });
        setErrors((prev: any) => ({
            ...prev,
            DNI: error
        }));
    };

    const handleKindOfDniChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setValues((prev: any) => ({
            ...prev,
            kindOfDNI: val,
            DNI: ''
        }));
        setErrors((prev: any) => ({
            ...prev,
            kindOfDNI: undefined,
            DNI: undefined
        }));
    };

    const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setValues((prev: any) => ({
            ...prev,
            iso2Country: val,
            phoneNumber: '',
            DNI: ''
        }));
        setErrors((prev: any) => ({
            ...prev,
            phoneNumber: undefined,
            DNI: undefined
        }));
    };

    useEffect(() => {
        if (show && customer) {
            const country = customer.isO2Country || 'VE';
            const countryObj = countries.find((c) => c.iso2Country === country) ?? null;
            const nationalPhone = formatPhoneNational(customer.phoneNumber || '', country, countryObj?.phoneCode);
            setValues({
                idCustomer: customer.id,
                firstName: customer.firstName || '',
                lastName: customer.lastName || '',
                email: customer.email || '',
                password: '',
                kindOfDNI: customer.kindOfDNI || '',
                DNI: customer.dni || '',
                phoneNumber: nationalPhone,
                isActive: customer.isActive,
                isVerify: customer.isVerify,
                iso2Country: country
            });
        } else if (!show) {
            resetForm();
        }
    }, [show, customer, countries, setValues, resetForm]);

    const onSubmit = async () => {
        try {
            setLoading(true);
            const submitData = { ...values };

            // Convert kindOfDNI from composite key back to raw kindOfDNI
            const selectedDniObj = findDniTypeByCompositeKey(dniTypes, values.kindOfDNI);
            if (selectedDniObj) {
                submitData.kindOfDNI = selectedDniObj.kindOfDNI;
            }

            // Prepend country phone code prefix on submit
            if (submitData.phoneNumber && selectedCountryObj?.phoneCode) {
                const code = selectedCountryObj.phoneCode.startsWith("+")
                    ? selectedCountryObj.phoneCode
                    : `+${selectedCountryObj.phoneCode}`;

                if (!submitData.phoneNumber.startsWith(code)) {
                    submitData.phoneNumber = `${code}${submitData.phoneNumber}`;
                }
            }

            if (!submitData.password) {
                delete submitData.password;
            }

            const response = await adminCustomerService.updateCustomerData(submitData);

            if (response.statusCode === 200) {
                onHide();
                onSuccess();
                await Swal.fire({
                    icon: 'success',
                    title: '¡Actualizado!',
                    text: 'Los datos del cliente han sido modificados correctamente.',
                    confirmButtonText: 'Aceptar',
                    confirmButtonColor: 'var(--elPaket-primary-color)'
                });
            } else {
                errorService.showErrorAlert(response, 'Error al Actualizar Cliente');
            }
        } catch (error: any) {
            console.error('Error in UpdateCustomerModal:', error);
            errorService.showErrorAlert(error, 'Error de Conexión');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className={styles.updateCustomerModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    Editar datos del cliente
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className={styles.modalSubtitle}>
                    Modifica los campos del perfil del cliente a continuación.
                </p>
                <Form onSubmit={handleSubmit(onSubmit)} id="update-customer-form" noValidate>
                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Nombre</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="firstName"
                                    placeholder="Nombre"
                                    value={values.firstName}
                                    onChange={handleChange}
                                    className={`${styles.updateCustomerModalInput} ${errors.firstName ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.firstName && <div className={styles.errorText}>{errors.firstName}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Apellido</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="lastName"
                                    placeholder="Apellido"
                                    value={values.lastName}
                                    onChange={handleChange}
                                    className={`${styles.updateCustomerModalInput} ${errors.lastName ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.lastName && <div className={styles.errorText}>{errors.lastName}</div>}
                        </Form.Group>
                    </div>

                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Correo Electrónico</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
                                <Form.Control
                                    type="email"
                                    name="email"
                                    placeholder="ejemplo@correo.com"
                                    value={values.email}
                                    onChange={handleChange}
                                    className={`${styles.updateCustomerModalInput} ${errors.email ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.email && <div className={styles.errorText}>{errors.email}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Nueva Contraseña</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="Dejar en blanco para mantener actual"
                                    value={values.password}
                                    onChange={handleChange}
                                    className={`${styles.updateCustomerModalInput} ${errors.password ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className={styles.passwordToggle}
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </button>
                            </div>
                            {errors.password && <div className={styles.errorText}>{errors.password}</div>}
                        </Form.Group>
                    </div>

                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Tipo de Documento</Form.Label>
                            <div className={styles.inputWrapper}>
                                <Form.Select
                                    name="kindOfDNI"
                                    value={values.kindOfDNI}
                                    onChange={handleKindOfDniChange}
                                    className={styles.updateCustomerModalInput}
                                    style={{ paddingLeft: '14px', paddingRight: '14px' }}
                                    disabled={loading || loadingDniTypes}
                                >
                                    {loadingDniTypes && !values.kindOfDNI ? (
                                        <option value="">...</option>
                                    ) : (
                                        <>
                                            <option value="" disabled>
                                                Seleccione un tipo de DNI
                                            </option>
                                            {values.kindOfDNI && !dniTypes.some((t) => t.kindOfDNI === values.kindOfDNI || getDniCompositeKey(t) === values.kindOfDNI) && (
                                                <option value={values.kindOfDNI}>
                                                    {values.iso2Country === 'VE'
                                                        ? (findDniTypeByCompositeKey(dniTypes, values.kindOfDNI)?.prefix || values.kindOfDNI)
                                                        : (findDniTypeByCompositeKey(dniTypes, values.kindOfDNI)?.kindOfDNI || values.kindOfDNI)}
                                                </option>
                                            )}
                                            {dniTypes.map((type) => (
                                                <option key={getDniCompositeKey(type)} value={getDniCompositeKey(type)}>
                                                    {values.iso2Country === 'VE' ? (type.prefix || type.kindOfDNI) : type.kindOfDNI}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </Form.Select>
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Número de Documento (DNI)</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faAddressCard} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="DNI"
                                    placeholder="DNI"
                                    value={values.DNI}
                                    onChange={handleDniChange}
                                    maxLength={getMaxLengthFromMask(findDniTypeByCompositeKey(dniTypes, values.kindOfDNI)?.mask) || 20}
                                    className={`${styles.updateCustomerModalInput} ${errors.DNI ? styles.inputError : ''}`}
                                    disabled={loading || loadingDniTypes}
                                />
                            </div>
                            {errors.DNI && <div className={styles.errorText}>{errors.DNI}</div>}
                        </Form.Group>
                    </div>

                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Teléfono</Form.Label>
                            <div className={`${styles.phoneInputWrapper} ${errors.phoneNumber ? styles.isInvalidWrapper : ''}`}>
                                {values.iso2Country && (
                                    <div className={styles.phonePrefix}>
                                        <img
                                            src={`https://flagcdn.com/w40/${values.iso2Country.toLowerCase()}.png`}
                                            srcSet={`https://flagcdn.com/w80/${values.iso2Country.toLowerCase()}.png 2x`}
                                            width="20"
                                            height="14"
                                            alt={values.iso2Country}
                                            className={styles.phonePrefixFlag}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                        {selectedCountryObj?.phoneCode && (
                                            <span className={styles.phonePrefixCode}>
                                                {selectedCountryObj.phoneCode.startsWith("+")
                                                    ? selectedCountryObj.phoneCode
                                                    : `+${selectedCountryObj.phoneCode}`}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <Form.Control
                                    type="tel"
                                    name="phoneNumber"
                                    placeholder={values.iso2Country ? `Ej: ${getPhonePlaceholder(values.iso2Country)}` : "Selecciona un país primero"}
                                    value={values.phoneNumber}
                                    onChange={handlePhoneChange}
                                    disabled={loading}
                                />
                            </div>
                            {errors.phoneNumber && <div className={styles.errorText}>{errors.phoneNumber}</div>}
                        </Form.Group>
                    </div>
                </Form>
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                <Button
                    variant="secondary"
                    onClick={onHide}
                    className={styles.secondaryBtn}
                    disabled={loading}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    type="submit"
                    form="update-customer-form"
                    className={modalStyles.closeBtn}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Guardando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="me-2" /> Guardar Cambios
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateCustomerModal;
